import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Users, Truck, Clock, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const EUR = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

export default function AiQuoteDraft({ onApply, onPrice }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/quote-draft", { description: text }, { timeout: 120000 });
      setDraft(data);
      onApply(data);
      toast.success("Bozza compilata: controlla i campi qui sotto");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-violet-50/70 border border-violet-200 rounded-2xl p-4 space-y-3" data-testid="ai-quote-draft">
      <div className="flex items-center gap-2 font-display font-bold text-violet-900">
        <Sparkles className="w-4 h-4" /> Preventivo dalla descrizione
      </div>
      <p className="text-xs text-violet-800/80">Incolla il messaggio, l'email o gli appunti della telefonata: ChatGPT compila il lavoro e propone squadra, furgoni, ore e prezzo. Decidi tu cosa tenere.</p>
      <Textarea
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Es: Buongiorno, sono Maria Rossi, devo traslocare un bilocale al 3° piano senza ascensore da Via Roma 10 a Via Verdi 4, Milano, sabato prossimo. Ho un divano, un letto matrimoniale da smontare, lavatrice e circa 30 scatoloni…"
        className="bg-white"
        data-testid="ai-quote-input"
      />
      <div className="flex justify-end">
        <Button type="button" onClick={run} disabled={loading || text.trim().length < 10} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="ai-quote-generate-button">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {loading ? "ChatGPT sta preparando…" : "Prepara con ChatGPT"}
        </Button>
      </div>
      {draft && <DraftSummary draft={draft} onPrice={onPrice} />}
    </div>
  );
}

function DraftSummary({ draft, onPrice }) {
  return (
    <div className="bg-white border border-violet-200 rounded-xl p-4 space-y-3" data-testid="ai-quote-result">
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat icon={Users} label="Persone" value={draft.crew_size || "—"} testid="ai-quote-crew" />
        <Stat icon={Truck} label="Furgoni" value={draft.vans || "—"} testid="ai-quote-vans" />
        <Stat icon={Clock} label="Ore" value={draft.estimated_hours || "—"} testid="ai-quote-hours" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-violet-100 pt-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Prezzo suggerito</div>
          <div className="font-display text-2xl font-extrabold text-violet-900" data-testid="ai-quote-price">
            {draft.suggested_total > 0 ? EUR.format(draft.suggested_total) : "Storico insufficiente"}
          </div>
        </div>
        {draft.suggested_total > 0 && (
          <Button type="button" variant="outline" onClick={() => onPrice(draft.suggested_total)} data-testid="ai-quote-use-price-button">
            Usa come totale
          </Button>
        )}
      </div>
      {draft.price_reasoning && <p className="text-xs text-slate-600">{draft.price_reasoning}</p>}
      {draft.missing_info?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ai-quote-missing">
          <div className="flex items-center gap-1.5 text-xs font-bold text-amber-900 mb-1"><HelpCircle className="w-3.5 h-3.5" /> Da chiedere al cliente</div>
          <ul className="list-disc pl-5 text-xs text-amber-900 space-y-0.5">
            {draft.missing_info.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-violet-50 rounded-lg py-2">
      <Icon className="w-4 h-4 mx-auto text-violet-600" />
      <div className="font-display text-xl font-extrabold text-slate-900" data-testid={testid}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    </div>
  );
}
